import { Fragment, useMemo, useRef } from 'react';

import CommonImage from '@/components/CommonImage';
import {
	Task_PRIORITY_OPTIONS,
	dashboardGroupBy,
	formatDashboardDate,
	handleTextareaHeight
} from '@/features/my-dashborad/utils';
import HorizontalScroll from '@/components/HorizontalScroll';
import { getShortName } from '@/utils/Util';

const FactoryDevelopmentList = ({
	listViewResponse,
	onClickDesignImage,
	handleSelectSampleItem,
	handleSelectAllSampleItem,
	selectedSampleItem,
	handleUpdatePriority,
	handleGetRemarks,
	isUpdatingRemarks,
	selectedGroupBy = null
}) => {
	const FACTORY_DEVELOPMENT_TABLE_COL_SPAN = 23;
	const rows = Array.isArray(listViewResponse) ? listViewResponse : [];
	const groupedRows = useMemo(() => {
		if (!selectedGroupBy?.id || !rows.length) return null;
		return dashboardGroupBy(rows, selectedGroupBy.id);
	}, [rows, selectedGroupBy?.id]);
	const displayGroupEntries = useMemo(() => {
		if (!groupedRows) return null;
		return Object.entries(groupedRows);
	}, [groupedRows]);
	const elementRef = useRef(null);

	const PriorityDropdown = ({ priority, productItemData, handleUpdatePriority }) => (
		<div className="option">
			<div className="dropdown">
				<button
					aria-expanded="false"
					className="btn btn-default dropdown-toggle"
					data-toggle="dropdown"
					id="menu1"
					type="button"
				>
					<span
						className={`mr-2 status yellow-1 status-with-border  ${Task_PRIORITY_OPTIONS[priority]}`}
					>
						{priority || 'Select priority'}
					</span>
					<CommonImage
						className="arrow-icon"
						src="/icons/down-arrow-gray-dark-01.svg"
						width={20}
						height={20}
					/>
				</button>
				<ul
					aria-labelledby="menu1"
					className="dropdown-menu dropdown-menu-right shadow-lg"
					role="menu"
					x-placement="bottom-end"
				>
					{Task_PRIORITY_OPTIONS?.map((tag) => (
						<li
							key={tag.value}
							className="px-4 pb-3 pt-3"
							role="presentation"
							onClick={() =>
								handleUpdatePriority({
									...productItemData,
									taskPriority: tag.value
								})
							}
						>
							<span
								className={`font-weight-normal ${tag.className}`}
								role="menuitem"
								tabIndex={-1}
							>
								{tag?.name}
							</span>
						</li>
					))}
				</ul>
			</div>
		</div>
	);

	const renderFactoryDevelopmentListRow = (item, idx) => {
		const sampleDetails = item?.factoryDevelopmentJourneyDetails?.sampleItemDetailsList ?? [];
		const detailLineList = sampleDetails
			.map((detail) => {
				const color = detail?.colorResponse?.name;
				const size = detail?.size;
				const quantity = detail?.quantity;
				return `${color ? `${color} - ` : ''}${size ? `${size} - ` : ''}${quantity ? `${quantity} Pcs` : ''}`;
			})
			.filter(Boolean);

		return (
			<tr key={`${item.id}_${idx}`}>
				<td className="checkbox-column">
					<div className="custom-chekbox">
						<div className="form-group m-0">
							<input
								type="checkbox"
								id={`${item?.id}-${idx}`}
								name={`sample_${item?.id}`}
								value={item?.id}
								onChange={(e) => handleSelectSampleItem(e, item)}
								checked={selectedSampleItem.includes(parseInt(item?.id))}
							/>
							<label htmlFor={`${item?.id}-${idx}`} />
						</div>
					</div>
				</td>
				<td className="image-column cursor-pointer" onClick={() => onClickDesignImage(item?.id)}>
					<div className="d-flex align-items-center justify-content-center">
						<img src={item.designDocuments?.[0]?.docUrl} alt="" className="cell-img pr-2" />
					</div>
				</td>
				<td className="ref-column">
					<p className="regular-14 gray_dark_02 mb-0">{item?.systemReferenceNumber}</p>
				</td>
				<td className="brand-column">
					<p className="regular-14 gray_dark_02 mb-0">
						{item?.factoryDevelopmentJourneyDetails?.brandResponse?.name}
					</p>
				</td>
				<td className="request-column">
					<p
						className="regular-14 gray_dark_02 mb-0 cursor-pointer text-underline"
						onClick={() =>
							window.open(
								`/sample/view/${item?.factoryDevelopmentJourneyDetails?.requestId}`,
								'_blank'
							)
						}
					>
						{item?.factoryDevelopmentJourneyDetails?.requestName}
					</p>
				</td>
				<td className="style-details-column">
					<p className="regular-14  gray_dark_02 mb-0">{item?.productGroup}</p>
					<p className="regular-14  gray_dark_02 mb-0">{item?.mainFabricType}</p>
					<p className="regular-14  gray_dark_02 mb-0">{item?.addedByName}</p>
				</td>
				<td className="development-type-column">
					<p className="regular-14 gray_dark_02 mb-0">
						{item?.factoryDevelopmentJourneyDetails?.sampleDevelopmentType
							?.replaceAll('_', ' ')
							?.toLowerCase()
							?.replace(/\b\w/g, (char) => char.toUpperCase())}
					</p>
				</td>
				<td className="color-size-qty-column date-column">
					{detailLineList.map((line, detailIdx) => (
						<div className="d-flex align-items-center" key={`${item.id}_detail_${detailIdx}`}>
							<span className="dot d-inline-block mt-2"></span>
							<p className="regular-14 gray_dark_02">{line}</p>
						</div>
					))}
				</td>
				<td className="fabric-details-column">
					<p className="regular-14 gray_dark_02 mb-0">
						{item?.mainFabricType
							? item.mainFabricType.toLowerCase().charAt(0).toUpperCase() +
								item.mainFabricType.toLowerCase().slice(1)
							: ''}
						,
						{item?.subCategory
							? item?.subCategory.toLowerCase().charAt(0).toUpperCase() +
								item?.subCategory.toLowerCase().slice(1)
							: ''}
						,
						{item?.fabricDetails
							? item?.fabricDetails.toLowerCase().charAt(0).toUpperCase() +
								item?.fabricDetails.toLowerCase().slice(1)
							: ''}
					</p>
				</td>
				<td className="print-embo-wash-column">
					<p className="regular-14 gray_dark_02 mb-0">
						<span className="regular-14 gray_dark_02 mb-0 d-block">
							{item?.factoryDevelopmentJourneyDetails?.printTypes?.join(', ')}
						</span>

						<span className="regular-14 gray_dark_02 mb-0 d-block">
							{item?.factoryDevelopmentJourneyDetails?.emboTypes?.join(', ')}
						</span>

						<span className="regular-14 gray_dark_02 mb-0 d-block">
							{item?.factoryDevelopmentJourneyDetails?.washTypes?.join(', ')}
						</span>
					</p>
				</td>
				<td className="planned-vs-actual-column date-column">
					{item?.factoryDevelopmentJourneyDetails?.requiredDate && (
						<p className="regular-14 gray_dark_02 mb-0">
							Required:{' '}
							{formatDashboardDate(item?.factoryDevelopmentJourneyDetails?.requiredDate)}
						</p>
					)}
					{item?.factoryDevelopmentJourneyDetails?.plannedDate && (
						<p className="regular-14 gray_dark_02 mb-0">
							Planned:{' '}
							{formatDashboardDate(item?.factoryDevelopmentJourneyDetails?.plannedDate)}
						</p>
					)}
					{item?.factoryDevelopmentJourneyDetails?.actualHandoverDate && (
						<p className="regular-14 gray_dark_02 mb-0">
							{`Actual H/O: ${formatDashboardDate(item?.factoryDevelopmentJourneyDetails?.actualHandoverDate)}`}
						</p>
					)}
				</td>
				<td className="request-rev-column date-column">
					{item?.factoryDevelopmentJourneyDetails?.handoverToProjectDate && (
						<p className="regular-14 gray_dark_02 mb-0">
							H/O to project team:{' '}
							{formatDashboardDate(
								item?.factoryDevelopmentJourneyDetails?.handoverToProjectDate
							)}
							,{item?.factoryDevelopmentJourneyDetails?.handoverToProjectName}
						</p>
					)}
					{item?.factoryDevelopmentJourneyDetails?.projectReceivedDate && (
						<p className="regular-14 gray_dark_02 mb-0">
							Received:{' '}
							{formatDashboardDate(item?.factoryDevelopmentJourneyDetails?.projectReceivedDate)}
							,{item?.factoryDevelopmentJourneyDetails?.projectReceivedByName}
						</p>
					)}
					{item?.factoryDevelopmentJourneyDetails?.gtReviewDate && (
						<p className="regular-14 gray_dark_02 mb-0">
							{`GT review: ${formatDashboardDate(item?.factoryDevelopmentJourneyDetails?.gtReviewDate)},
							${item?.factoryDevelopmentJourneyDetails?.gtReviewName}`}
						</p>
					)}
				</td>
				<td className="factory-ho-rev-column date-column">
					{item?.factoryDevelopmentJourneyDetails?.factoryName && (
						<p className="regular-14 gray_dark_02 mb-0">
							Factory:{item?.factoryDevelopmentJourneyDetails?.factoryName},{' '}
						</p>
					)}

					{item?.factoryDevelopmentJourneyDetails?.handoverToFactoryDate && (
						<p className="regular-14 gray_dark_02 mb-0">
							{`Send: ${formatDashboardDate(item?.factoryDevelopmentJourneyDetails?.handoverToFactoryDate)}, `}
							{item?.factoryDevelopmentJourneyDetails?.handoverToFactoryByName}
						</p>
					)}
					{item?.factoryDevelopmentJourneyDetails?.receiveFromFactoryDate && (
						<p className="regular-14 gray_dark_02 mb-0">
							{`Received: ${formatDashboardDate(item?.factoryDevelopmentJourneyDetails?.receiveFromFactoryDate)}, `}
							{item?.factoryDevelopmentJourneyDetails?.receiveFromFactoryByName}
						</p>
					)}
				</td>
				<td className="review-ho-to-am-column date-column">
					{item?.factoryDevelopmentJourneyDetails?.partialDevDate && (
						<p className="regular-14 gray_dark_02 mb-0">
							Partial Dev:{' '}
							{formatDashboardDate(item?.factoryDevelopmentJourneyDetails?.partialDevDate)},
							{item?.factoryDevelopmentJourneyDetails?.partialDevByName}
						</p>
					)}

					{item?.factoryDevelopmentJourneyDetails?.gtSampleReviewDate && (
						<p className="regular-14 gray_dark_02 mb-0">
							GT sample review:{' '}
							{formatDashboardDate(item?.factoryDevelopmentJourneyDetails?.gtSampleReviewDate)},
							{item?.factoryDevelopmentJourneyDetails?.gtSampleReviewByName}
						</p>
					)}
					{item?.factoryDevelopmentJourneyDetails?.actualHandoverDate && (
						<p className="regular-14 gray_dark_02 mb-0">
							{`H/O to AM: ${formatDashboardDate(item?.factoryDevelopmentJourneyDetails?.actualHandoverDate)}`}
							,{item?.factoryDevelopmentJourneyDetails?.handoverToAmByName}
						</p>
					)}
				</td>
				<td className="buyer-review-column date-column">
					{item?.factoryDevelopmentJourneyDetails?.sendToBuyerDate && (
						<p className="regular-14 gray_dark_02 mb-0">
							{`Send to buyer: ${formatDashboardDate(item?.factoryDevelopmentJourneyDetails?.sendToBuyerDate)}, `}
							{item?.factoryDevelopmentJourneyDetails?.sendToBuyerByName}
						</p>
					)}
					{item?.factoryDevelopmentJourneyDetails?.buyerReviewDate && (
						<p className="regular-14 gray_dark_02 mb-0">
							{`Buyer review: ${formatDashboardDate(item?.factoryDevelopmentJourneyDetails?.buyerReviewDate)}, `}
							{item?.factoryDevelopmentJourneyDetails?.buyerReviewByName}
						</p>
					)}
				</td>
				<td className="buyer-review-status-column date-column">
					{item?.factoryDevelopmentJourneyDetails?.orderExpectedDate && (
						<p className="regular-14 gray_dark_02 mb-0">
							Order expected:{' '}
							{formatDashboardDate(item?.factoryDevelopmentJourneyDetails?.orderExpectedDate)},
							{item?.factoryDevelopmentJourneyDetails?.orderExpectedUpdatedByName}
						</p>
					)}
					{item?.factoryDevelopmentJourneyDetails?.developmentClosedWithOrderDate && (
						<p className="regular-14 gray_dark_02 mb-0">
							Development closed with order:{' '}
							{formatDashboardDate(
								item?.factoryDevelopmentJourneyDetails?.developmentClosedWithOrderDate
							)}
						</p>
					)}
				</td>
				<td className="actual-lead-time-column">
					<p className="regular-14 gray_dark_02 mb-0">
						{item?.factoryDevelopmentJourneyDetails?.actualLeadTime}d
					</p>
				</td>
				<td className="plan-lead-time-column">
					{(() => {
						const actual = item?.factoryDevelopmentJourneyDetails?.actualLeadTime;
						const planned = item?.factoryDevelopmentJourneyDetails?.plannedLeadTime;
						const variance = actual - planned;

						return (
							<>
								<p className="regular-14  gray_dark_02 mb-0">
									{planned}D (
									<span className="regular-14  gray_dark_02 mb-0">{variance}</span>)
								</p>
								{variance === 0 ? (
									<span className="status green-3 status-with-border">on time</span>
								) : variance >= 1 ? (
									<span className="status red-3 status-with-border">Delayed</span>
								) : (
									<span className="status green-3 status-with-border">Early</span>
								)}
							</>
						);
					})()}
				</td>
				<td className="required-lead-time-column">
					{(() => {
						const actual = item?.factoryDevelopmentJourneyDetails?.actualLeadTime;
						const req = item?.factoryDevelopmentJourneyDetails?.requiredLeadTime;
						const variance = actual - req;
						return (
							<>
								<p className="regular-14  gray_dark_02 mb-0">
									{req}D (<span className="regular-14  gray_dark_02 mb-0">{variance}</span>)
								</p>
								{variance === 0 ? (
									<span className="status green-3 status-with-border">on time</span>
								) : variance >= 1 ? (
									<span className="status red-3 status-with-border">Delayed</span>
								) : (
									<span className="status green-3 status-with-border">Early</span>
								)}
							</>
						);
					})()}
				</td>
				<td className="last-status-column">
					<div>
						<span className="status yellow-1 status-with-border d-inline-block mb-1">
							{item?.factoryDevelopmentJourneyDetails?.lastStatusName}
						</span>
						<p className="regular-14 gray_dark_02 mb-0">
							{formatDashboardDate(item?.factoryDevelopmentJourneyDetails?.lastStatusDate)}
						</p>
						<p className="regular-14 gray_dark_02 mb-0">
							by {item?.factoryDevelopmentJourneyDetails?.lastStatusByName}
						</p>
					</div>
				</td>
				<td className="pending-issue-column">
					<p className="regular-14 gray_dark_02 mb-0">
						{item?.factoryDevelopmentJourneyDetails?.pendingIssueCount}
					</p>
				</td>
				<td className="priority-column">
					<PriorityDropdown
						priority={item?.factoryDevelopmentJourneyDetails?.taskPriority}
						productItemData={item}
						handleUpdatePriority={handleUpdatePriority}
					/>
				</td>
				<td className="remarks-column text-field-column">
					<textarea
						type="text"
						className="regular-14 gray_dark_02 mb-0"
						placeholder="Remarks"
						onChange={handleTextareaHeight}
						defaultValue={item?.factoryDevelopmentJourneyDetails?.remarks ?? ''}
						onBlur={(e) => handleGetRemarks(item, e.target.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter') e.currentTarget.blur();
						}}
						disabled={isUpdatingRemarks}
					/>
				</td>
			</tr>
		);
	};

	return (
		<div>
			<div className="list-view-table-container position-relative mt-4 mb-4">
				<HorizontalScroll elementReference={elementRef.current} />
				<div className="factory-development-details-view-table scroll-x-label" ref={elementRef}>
					<table className="table with-border">
						<thead>
							<tr>
								<th>
									<div className="custom-chekbox">
										<div className="form-group m-0">
											<input
												type="checkbox"
												id="selectAll"
												name="selectAll"
												onChange={(e) => handleSelectAllSampleItem(e)}
												checked={
													rows.length > 0 &&
													rows.every((item) =>
														selectedSampleItem.includes(parseInt(item?.id))
													)
												}
											/>
											<label htmlFor="selectAll" />
										</div>
									</div>
								</th>
								<th>Image</th>
								<th>Ref</th>
								<th>Brand</th>
								<th>Request</th>
								<th className="white-space-nowrap">Style details</th>
								<th className="white-space-nowrap">Development type</th>
								<th className="white-space-nowrap">Color -Size -Qty</th>
								<th className="white-space-nowrap">Fabric details</th>
								<th className="white-space-nowrap">Print, Embo, Wash</th>
								<th className="white-space-nowrap">Planned vs Actual</th>
								<th className="white-space-nowrap">Request &amp; Rev</th>
								<th className="white-space-nowrap">Factory H/O &amp; Rev</th>
								<th className="white-space-nowrap">Review &amp; H/O to AM</th>
								<th className="white-space-nowrap">Buyer review</th>
								<th className="white-space-nowrap">Buyer review status</th>
								<th>
									<span className="white-space-nowrap">Actual lead time:</span>
									<span className="white-space-wrap">H/O to project &gt; H/O to Owner</span>
								</th>
								<th>
									<span className="white-space-nowrap">Plan lead time:</span>
									<span className="white-space-wrap">H/O to project &gt; Plan date</span>
								</th>
								<th>
									<span className="white-space-nowrap">Required lead time:</span>
									<span className="white-space-wrap">H/O to project &gt; Required</span>
								</th>
								<th className="white-space-nowrap">Last status</th>
								<th className="white-space-nowrap">Pending issue</th>
								<th>Priority</th>
								<th>Remarks</th>
							</tr>
						</thead>
						<tbody>
							{displayGroupEntries?.length
								? displayGroupEntries.map(([groupName, items]) => (
										<Fragment key={`group_${groupName}`}>
											<tr className="sample-journey-group-header-row">
												<td
													colSpan={FACTORY_DEVELOPMENT_TABLE_COL_SPAN}
													className="bg-white group-title-header border-bottom-0 pt-3 pb-2"
												>
													<div className="group-title-header-content">
														<span className="semibold-14 mr-2 gray_dark_01">
															{selectedGroupBy?.name === 'Request-wise' ? (
																<span
																	className="text-underline cursor-pointer"
																	onClick={() =>
																		window.open(
																			`/sample/view/${items?.[0]?.factoryDevelopmentJourneyDetails?.requestId}`,
																			'_blank'
																		)
																	}
																>
																	{getShortName(groupName, 50)}
																</span>
															) : (
																<span className="gray_dark_01">
																	{getShortName(groupName, 50)}
																</span>
															)}

															<span className="regular-14 gray_dark_01">
																({items?.length ?? 0})
															</span>
														</span>
													</div>
												</td>
											</tr>
											{items?.map((item, idx) =>
												renderFactoryDevelopmentListRow(item, idx)
											)}
										</Fragment>
									))
								: rows.map((item, idx) => renderFactoryDevelopmentListRow(item, idx))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
};

export default FactoryDevelopmentList;
